/**
 * HubSpot-style column selector modal: searchable available fields (left),
 * selected columns with reorder/remove (right), Apply / Cancel / Reset.
 */

import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Grid,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconGripVertical, IconInfoCircle, IconX } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { SearchInput } from './SearchInput';

/** Minimal column shape the modal needs (any column with a stable key and a display label). */
export interface ColumnSelectOption {
  key: string;
  displayLabel: string;
  description?: string;
}

const COLUMN_DRAG_TYPE = 'COLUMN_ITEM';

interface DraggableColumnChipProps {
  columnKey: string;
  label: string;
  description?: string;
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  onRemove: () => void;
}

function ColumnInfoIcon({ label, description }: { label: string; description?: string }): JSX.Element | null {
  if (!description) return null;
  return (
    <Tooltip label={description} withArrow multiline maw={260}>
      <ActionIcon
        component="span"
        variant="transparent"
        color="gray"
        size="xs"
        aria-label={`${label} info`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <IconInfoCircle size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

function DraggableColumnChip({
  columnKey: _columnKey,
  label,
  description,
  index,
  moveColumn,
  onRemove,
}: DraggableColumnChipProps): JSX.Element {
  const [{ isDragging }, drag] = useDrag({
    type: COLUMN_DRAG_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: COLUMN_DRAG_TYPE,
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <Box
      ref={(node) => drag(drop(node))}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: 'var(--mantine-color-gray-0)',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
      }}
    >
      <ActionIcon variant="subtle" color="gray" size="sm" style={{ cursor: 'grab' }}>
        <IconGripVertical size={14} />
      </ActionIcon>
      <Text size="sm" style={{ flex: 1 }} truncate>
        {label}
      </Text>
      <ColumnInfoIcon label={label} description={description} />
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove ${label}`}
      >
        <IconX size={14} />
      </ActionIcon>
    </Box>
  );
}

export interface ColumnSelectModalProps {
  opened: boolean;
  onClose: () => void;
  allColumns: ColumnSelectOption[];
  defaultColumnKeys: string[];
  value: string[] | null;
  onApply: (value: string[] | null) => void;
  centered?: boolean;
  alwaysShowScrollbars?: boolean;
}

function getLabelByKey(allColumns: ColumnSelectOption[], key: string): string {
  return allColumns.find((c) => c.key === key)?.displayLabel ?? key;
}

export function ColumnSelectModal({
  opened,
  onClose,
  allColumns,
  defaultColumnKeys,
  value,
  onApply,
  centered = false,
  alwaysShowScrollbars = false,
}: ColumnSelectModalProps): JSX.Element {
  const [workingSelection, setWorkingSelection] = useState<string[]>(defaultColumnKeys);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (opened) {
      setWorkingSelection(value ?? defaultColumnKeys);
      setSearchQuery('');
    }
  }, [opened, value, defaultColumnKeys]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return allColumns;
    const q = searchQuery.toLowerCase();
    return allColumns.filter(
      (col) => col.displayLabel.toLowerCase().includes(q) || col.key.toLowerCase().includes(q)
    );
  }, [allColumns, searchQuery]);

  const selectedSet = useMemo(() => new Set(workingSelection), [workingSelection]);

  const handleToggle = (key: string, checked: boolean): void => {
    if (checked) {
      setWorkingSelection((prev) => (prev.includes(key) ? prev : [...prev, key]));
    } else {
      setWorkingSelection((prev) => prev.filter((k) => k !== key));
    }
  };

  const handleRemove = (key: string): void => {
    setWorkingSelection((prev) => prev.filter((k) => k !== key));
  };

  const handleApply = (): void => {
    const toApply = workingSelection.length === 0 ? null : workingSelection;
    onApply(toApply);
    onClose();
  };

  const handleCancel = (): void => {
    setWorkingSelection(value ?? defaultColumnKeys);
    onClose();
  };

  const handleReset = (): void => {
    setWorkingSelection([...defaultColumnKeys]);
  };

  const moveColumn = (dragIndex: number, hoverIndex: number): void => {
    setWorkingSelection((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, removed);
      return next;
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group justify="space-between" wrap="nowrap" style={{ width: '100%', flex: 1 }}>
          <Title order={3} c="gray.9">
            Choose which columns you see
          </Title>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleCancel}
            aria-label="Close"
            size="md"
            ml="auto"
          >
            <IconX size={20} stroke={1.5} />
          </ActionIcon>
        </Group>
      }
      size="lg"
      centered={centered}
      withCloseButton={false}
      styles={{ header: { width: '100%' }, title: { flex: 1 } }}
    >
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Text size="sm" fw={600} mb="xs" c="gray.9">
            Available fields
          </Text>
          <SearchInput
            placeholder="Search columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            mb="sm"
          />
          <ScrollArea.Autosize
            mah={320}
            type={alwaysShowScrollbars ? 'always' : 'scroll'}
            offsetScrollbars
          >
            <Stack gap="xs">
              {filteredColumns.map((col) => (
                <Checkbox
                  key={col.key}
                  label={
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm">{col.displayLabel}</Text>
                      <ColumnInfoIcon label={col.displayLabel} description={col.description} />
                    </Group>
                  }
                  checked={selectedSet.has(col.key)}
                  onChange={(e) => handleToggle(col.key, e.currentTarget.checked)}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Text size="sm" fw={600} mb="xs" c="gray.9">
            Selected columns ({workingSelection.length})
          </Text>
          <ScrollArea.Autosize
            mah={370}
            type={alwaysShowScrollbars ? 'always' : 'scroll'}
            offsetScrollbars
          >
            <DndProvider backend={HTML5Backend}>
              <Stack gap="xs">
                {workingSelection.map((key, index) => (
                  <DraggableColumnChip
                    key={key}
                    columnKey={key}
                    label={getLabelByKey(allColumns, key)}
                    description={allColumns.find((column) => column.key === key)?.description}
                    index={index}
                    moveColumn={moveColumn}
                    onRemove={() => handleRemove(key)}
                  />
                ))}
              </Stack>
            </DndProvider>
          </ScrollArea.Autosize>
        </Grid.Col>
      </Grid>

      <Group justify="space-between" mt="xl" wrap="wrap" gap="sm">
        <Group gap="xs">
          <Button variant="light" color="gold" onClick={handleApply}>
            Apply
          </Button>
          <Button variant="light" color="gray" onClick={handleCancel}>
            Cancel
          </Button>
        </Group>
        <Button variant="subtle" color="gray" onClick={handleReset}>
          Reset
        </Button>
      </Group>
    </Modal>
  );
}
